import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Database, Brain, FileText, UserCheck, ShieldCheck, ArrowRight, Loader2,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const PIPELINE = [
  { icon: Database, label: "Data Collected", desc: "Patient & case data" },
  { icon: Brain, label: "AI Analysis", desc: "Clinical reasoning" },
  { icon: FileText, label: "Draft Generated", desc: "Structured report" },
  { icon: UserCheck, label: "Doctor Review", desc: "Clinician edits" },
  { icon: ShieldCheck, label: "Approved", desc: "Final report" },
];

export default function ContahaAI() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [type, setType] = useState("Implant Assessment");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/ai/pipeline").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/cases").then((r) => setCases(r.data)); }, []);

  const generate = async () => {
    if (!caseId) return toast.error("Select a case");
    setBusy(true);
    try { const { data: rep } = await api.post("/ai/report/generate", { case_id: caseId, type }); toast.success("AI report drafted"); navigate(`/reports/${rep.id}`); }
    catch { toast.error("Generation failed"); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="CONTAHA AI" subtitle="AI-assisted clinical documentation workspace" breadcrumb={["Intelligence", "CONTAHA AI"]} />

      {/* Pipeline */}
      <SurfaceCard dark className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-5"><Sparkles className="h-4 w-4 text-[#3FD98A]" /><span className="text-sm font-bold text-white">AI Report Pipeline</span><span className="text-xs text-[#8B9698]">· AI assists, clinician decides</span></div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {PIPELINE.map((s, i) => (
            <div key={i} className="flex flex-1 items-center gap-2">
              <div className="flex-1 rounded-xl border border-[#223139] bg-[#07131A]/60 p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00A85A]/15 mb-2"><s.icon className="h-4 w-4 text-[#3FD98A]" /></div>
                <div className="text-xs font-bold text-white">{s.label}</div>
                <div className="text-[10px] text-[#8B9698]">{s.desc}</div>
              </div>
              {i < PIPELINE.length - 1 && <ArrowRight className="h-4 w-4 text-[#4E5F68] shrink-0 rotate-90 sm:rotate-0" />}
            </div>
          ))}
        </div>
      </SurfaceCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator */}
        <SurfaceCard className="p-5">
          <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Generate a Report</h3>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Case</Label>
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger className="mt-1.5" data-testid="ai-case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.patient_name} · {c.case_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold">Report template</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5" data-testid="ai-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Clinical Report", "Implant Assessment", "Treatment Plan", "Post-Operative Report", "Consultation Summary"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={busy} data-testid="ai-generate-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Drafting...</> : <><Sparkles className="h-4 w-4 mr-1.5" />Draft with AI</>}
            </Button>
          </div>
        </SurfaceCard>

        {/* Stats */}
        <div className="lg:col-span-2 grid grid-cols-3 gap-4 h-fit">
          {[
            { label: "Pending Review", value: data?.pending_review ?? "—", tone: "text-amber-500" },
            { label: "Approved", value: data?.approved ?? "—", tone: "text-[#00A85A]" },
            { label: "Total Reports", value: data?.total ?? "—", tone: "text-[#101516] dark:text-white" },
          ].map((s) => (
            <SurfaceCard key={s.label} className="p-5"><div className={`text-3xl font-extrabold ${s.tone}`}>{s.value}</div><div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-[#8B9698]">{s.label}</div></SurfaceCard>
          ))}
          <div className="col-span-3">
            <SurfaceCard className="p-5">
              <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-3">Recent AI Activity</h3>
              <div className="space-y-2">
                {(data?.recent || []).map((r) => (
                  <button key={r.id} onClick={() => navigate(`/reports/${r.id}`)} className="flex w-full items-center gap-3 rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3 text-left hover:border-[#00A85A]/40">
                    <FileText className="h-4 w-4 text-[#00A85A]" /><div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#101516] dark:text-white truncate">{r.title}</div><div className="text-[11px] text-[#8B9698]">{r.type} · {formatDate(r.created_at)}</div></div><StatusBadge status={r.status} /><ChevronRight className="h-4 w-4 text-[#8B9698]" />
                  </button>
                ))}
                {(!data?.recent || data.recent.length === 0) && <p className="text-sm text-[#8B9698] py-4 text-center">No AI activity yet.</p>}
              </div>
            </SurfaceCard>
          </div>
        </div>
      </div>
    </div>
  );
}
